import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Search, RefreshCw, Upload, Eye, Trash2, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, X, AlertTriangle, CheckCircle, Clock, Database,
  Mail, Edit, Key, ShieldCheck, BellRing
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LegacyUsersTab({ token }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, migrated: 0, pending: 0 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [detail, setDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePin, setDeletePin] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editing, setEditing] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  const fileInputRef = useRef(null);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error();
      setStats(await r.json());
    } catch {
      // Silencioso
    }
  }, [token]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (search) params.set('search', search);
      if (estadoFilter) params.set('estado', estadoFilter);
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error('Error al cargar');
      const data = await r.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      toast.error('Error al cargar usuarios antiguos');
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search, estadoFilter]);

  useEffect(() => {
    fetchStats();
    fetchList();
  }, [fetchStats, fetchList]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!confirm(`Va a importar el archivo "${file.name}". Los registros pendientes existentes serán reemplazados (los ya migrados se conservan). ¿Continuar?`)) {
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('replace', 'true');

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/superadmin/legacy-users/import`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };
      xhr.onload = async () => {
        setUploading(false);
        setUploadProgress(0);
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            toast.success(`Importación exitosa: ${data.inserted} registros cargados`);
          } catch {
            toast.success('Importación completada');
          }
          await Promise.all([fetchStats(), fetchList()]);
        } else {
          toast.error(`Error en la importación (${xhr.status})`);
        }
      };
      xhr.onerror = () => {
        setUploading(false);
        setUploadProgress(0);
        toast.error('Error de red al subir el archivo');
      };
      xhr.send(formData);
    } catch (err) {
      setUploading(false);
      toast.error('Error al subir el archivo');
    }
  };

  const handleViewDetail = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) throw new Error();
      setDetail(await r.json());
    } catch {
      toast.error('No se pudo cargar el detalle');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (!deletePin || deletePin.length < 4) {
      toast.error('Ingrese el PIN de eliminación');
      return;
    }
    setDeleting(true);
    try {
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pin: deletePin })
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al eliminar');
      }
      toast.success('Registro eliminado');
      setDeleteTarget(null);
      setDeletePin('');
      await Promise.all([fetchStats(), fetchList()]);
    } catch (e) {
      toast.error(e.message || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const handleNotify = async (item) => {
    if (!item.correo) {
      toast.error('Este registro no tiene email');
      return;
    }
    if (!confirm(`Enviar email recordatorio a ${item.correo}?`)) return;
    setActionLoadingId(item.id);
    try {
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users/${item.id}/notify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Error al notificar');
      toast.success(`Notificación enviada a ${data.email}`);
      await fetchList();
    } catch (e) {
      toast.error(e.message || 'Error al notificar');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleTempPassword = async (item) => {
    if (!item.correo) {
      toast.error('Este registro no tiene email');
      return;
    }
    if (!confirm(`Generar contraseña temporal para ${item.correo}?\n\nEsto creará/actualizará la cuenta y enviará la contraseña por email.`)) return;
    setActionLoadingId(item.id);
    try {
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users/${item.id}/temp-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Error al generar contraseña');
      setTempPasswordResult(data);
      toast.success('Contraseña temporal generada y enviada por email');
      await Promise.all([fetchStats(), fetchList()]);
    } catch (e) {
      toast.error(e.message || 'Error al generar contraseña');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleVerify = async (item) => {
    if (!item.correo) {
      toast.error('Este registro no tiene email');
      return;
    }
    if (!confirm(`Marcar como verificado a ${item.correo}?\n\nEsto creará la cuenta si aún no existe.`)) return;
    setActionLoadingId(item.id);
    try {
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users/${item.id}/verify`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Error al verificar');
      toast.success(`Usuario ${data.email} verificado`);
      await Promise.all([fetchStats(), fetchList()]);
    } catch (e) {
      toast.error(e.message || 'Error al verificar');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleEditSave = async () => {
    if (!editTarget) return;
    setEditing(true);
    try {
      const payload = {
        correo: editTarget.correo || '',
        primer_nombre: editTarget.primer_nombre || '',
        segundo_nombre: editTarget.segundo_nombre || '',
        primer_apellido: editTarget.primer_apellido || '',
        segundo_apellido: editTarget.segundo_apellido || '',
        telefono: editTarget.telefono || '',
        cargo: editTarget.cargo || '',
        numero_identificacion: editTarget.numero_identificacion || ''
      };
      const r = await fetch(`${API_URL}/api/superadmin/legacy-users/${editTarget.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Error al actualizar');
      toast.success('Registro actualizado');
      setEditTarget(null);
      await fetchList();
    } catch (e) {
      toast.error(e.message || 'Error al actualizar');
    } finally {
      setEditing(false);
    }
  };

  const fullName = (item) => [item.primer_nombre, item.segundo_nombre, item.primer_apellido, item.segundo_apellido]
    .filter(Boolean).join(' ');

  const formatDate = (s) => {
    if (!s) return '-';
    try {
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleDateString('es-CO', { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return s;
    }
  };

  return (
    <div className="space-y-6" data-testid="legacy-users-tab">
      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-blue-500" data-testid="stat-legacy-total">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total registros</p>
              <p className="text-3xl font-bold text-slate-900">{stats.total.toLocaleString('es-CO')}</p>
            </div>
            <Database className="h-10 w-10 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-amber-500" data-testid="stat-legacy-pending">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Pendientes de actualizar</p>
              <p className="text-3xl font-bold text-amber-600">{stats.pending.toLocaleString('es-CO')}</p>
            </div>
            <Clock className="h-10 w-10 text-amber-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-green-500" data-testid="stat-legacy-migrated">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Ya migrados</p>
              <p className="text-3xl font-bold text-green-600">{stats.migrated.toLocaleString('es-CO')}</p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-col md:flex-row gap-3 md:items-center">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Buscar por documento, email, nombre, cargo..."
              className="w-full pl-10 pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="legacy-search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Limpiar"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2" data-testid="legacy-search-btn">
            <Search className="h-4 w-4" /> Buscar
          </button>
        </form>

        <select
          value={estadoFilter}
          onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          data-testid="legacy-filter-estado"
        >
          <option value="">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="migrated">Ya migrados</option>
        </select>

        <button
          onClick={() => { fetchStats(); fetchList(); }}
          className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300 flex items-center gap-2"
          data-testid="legacy-refresh-btn"
        >
          <RefreshCw className="h-4 w-4" /> Actualizar
        </button>

        <button
          onClick={handleUploadClick}
          disabled={uploading}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
          data-testid="legacy-upload-btn"
        >
          <Upload className="h-4 w-4" />
          {uploading ? `Subiendo... ${uploadProgress}%` : 'Cargar CSV'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileSelected}
          className="hidden"
          data-testid="legacy-file-input"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b text-sm text-slate-600 flex items-center justify-between">
          <div>
            Mostrando <strong>{items.length}</strong> de <strong>{total.toLocaleString('es-CO')}</strong> registros
          </div>
          <div className="text-slate-500">Página {page} de {totalPages}</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Documento</th>
                <th className="text-left px-4 py-3 font-medium">Nombre completo</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Teléfono</th>
                <th className="text-left px-4 py-3 font-medium">Cargo</th>
                <th className="text-left px-4 py-3 font-medium">Estado</th>
                <th className="text-left px-4 py-3 font-medium">Fecha modif.</th>
                <th className="text-right px-4 py-3 font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10"><RefreshCw className="h-6 w-6 animate-spin mx-auto text-blue-500" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10 text-slate-500">Sin registros</td></tr>
              ) : items.map((item) => (
                <tr key={item.id} className={`border-t hover:bg-slate-50 ${item.notified_at && !item.migrated ? 'bg-blue-50/40' : ''}`} data-testid={`legacy-row-${item.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{item.numero_identificacion || '-'}</td>
                  <td className="px-4 py-3">{fullName(item) || <span className="text-slate-400">-</span>}</td>
                  <td className="px-4 py-3 text-slate-600">{item.correo || <span className="text-slate-400">-</span>}</td>
                  <td className="px-4 py-3">{item.telefono || '-'}</td>
                  <td className="px-4 py-3">{item.cargo || '-'}</td>
                  <td className="px-4 py-3">
                    {item.migrated ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                        <CheckCircle className="h-3 w-3" /> Actualizado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                        <Clock className="h-3 w-3" /> Pendiente
                      </span>
                    )}
                    {item.notified_at && (
                      <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium" title={`Notificado el ${formatDate(item.notified_at)}${item.notification_count > 1 ? ` (${item.notification_count} veces)` : ''}`} data-testid={`legacy-notified-badge-${item.id}`}>
                        <BellRing className="h-3 w-3" /> Notificado{item.notification_count > 1 ? ` (${item.notification_count})` : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatDate(item.fecha_modificacion)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => handleViewDetail(item.id)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Ver detalle"
                        data-testid={`legacy-view-${item.id}`}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setEditTarget({ ...item })}
                        className="p-1.5 text-slate-700 hover:bg-slate-100 rounded"
                        title="Editar"
                        data-testid={`legacy-edit-${item.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleNotify(item)}
                        disabled={actionLoadingId === item.id || !item.correo || item.migrated}
                        className={`p-1.5 rounded disabled:opacity-30 ${item.notified_at ? 'text-emerald-600 hover:bg-emerald-50' : 'text-amber-600 hover:bg-amber-50'}`}
                        title={item.notified_at ? `Re-notificar (ya notificado ${item.notification_count || 1}x)` : 'Notificar por email'}
                        data-testid={`legacy-notify-${item.id}`}
                      >
                        {item.notified_at ? <BellRing className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleTempPassword(item)}
                        disabled={actionLoadingId === item.id || !item.correo}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded disabled:opacity-30"
                        title="Contraseña temporal"
                        data-testid={`legacy-temp-pwd-${item.id}`}
                      >
                        <Key className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleVerify(item)}
                        disabled={actionLoadingId === item.id || !item.correo}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded disabled:opacity-30"
                        title="Verificar"
                        data-testid={`legacy-verify-${item.id}`}
                      >
                        <ShieldCheck className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => { setDeleteTarget(item); setDeletePin(''); }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Eliminar"
                        data-testid={`legacy-delete-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-5 py-3 border-t flex items-center justify-between text-sm">
          <div className="text-slate-500">Total: {total.toLocaleString('es-CO')}</div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid="legacy-page-first">
              <ChevronsLeft className="h-4 w-4" />
            </button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid="legacy-page-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="px-3">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid="legacy-page-next">
              <ChevronRight className="h-4 w-4" />
            </button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid="legacy-page-last">
              <ChevronsRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" data-testid="legacy-detail-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">Detalle de Usuario Antiguo</h3>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {[
                ['ID original (CSV)', detail.legacy_id],
                ['Documento', detail.numero_identificacion],
                ['Tipo identificación', detail.tipo_identificacion_id],
                ['Primer nombre', detail.primer_nombre],
                ['Segundo nombre', detail.segundo_nombre],
                ['Primer apellido', detail.primer_apellido],
                ['Segundo apellido', detail.segundo_apellido],
                ['Email', detail.correo],
                ['Teléfono', detail.telefono],
                ['Cargo', detail.cargo],
                ['Género (id)', detail.genero_id],
                ['Rol (id)', detail.rol_id],
                ['Persona migrante', detail.persona_migrante],
                ['País migrante (id)', detail.pais_migrante_id],
                ['Trabajos de cuidado', detail.trabajos_cuidado],
                ['Veterano', detail.veterano],
                ['Orientación sexual (id)', detail.orientacion_sexual_id],
                ['Fecha modificación', detail.fecha_modificacion],
                ['Importado el', detail.imported_at],
                ['Estado', detail.migrated ? 'Migrado' : 'Pendiente']
              ].map(([k, v]) => (
                <div key={k} className="border-b py-1">
                  <div className="text-slate-500 text-xs uppercase">{k}</div>
                  <div className="font-medium break-all">{v ?? <span className="text-slate-400">-</span>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Delete modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="legacy-delete-modal">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-semibold">Eliminar usuario antiguo</h3>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Va a eliminar permanentemente el registro de <strong>{fullName(deleteTarget) || deleteTarget.correo || 'usuario'}</strong>. Ingrese el PIN de eliminación para confirmar.
            </p>
            <input
              type="password"
              value={deletePin}
              onChange={(e) => setDeletePin(e.target.value)}
              placeholder="PIN de eliminación"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg mb-4"
              data-testid="legacy-delete-pin-input"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setDeleteTarget(null); setDeletePin(''); }} className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300">
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                data-testid="legacy-delete-confirm-btn"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto" data-testid="legacy-edit-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Editar Usuario Antiguo</h3>
              <button onClick={() => setEditTarget(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {[
                ['Email', 'correo', 'email'],
                ['Documento', 'numero_identificacion', 'text'],
                ['Primer Nombre', 'primer_nombre', 'text'],
                ['Segundo Nombre', 'segundo_nombre', 'text'],
                ['Primer Apellido', 'primer_apellido', 'text'],
                ['Segundo Apellido', 'segundo_apellido', 'text'],
                ['Teléfono', 'telefono', 'text'],
                ['Cargo', 'cargo', 'text']
              ].map(([label, key, type]) => (
                <div key={key}>
                  <label className="text-xs text-slate-500 uppercase block mb-1">{label}</label>
                  <input
                    type={type}
                    value={editTarget[key] || ''}
                    onChange={(e) => setEditTarget({ ...editTarget, [key]: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    data-testid={`legacy-edit-input-${key}`}
                  />
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button onClick={() => setEditTarget(null)} className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300">Cancelar</button>
              <button
                onClick={handleEditSave}
                disabled={editing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="legacy-edit-save-btn"
              >
                {editing ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Temp password result modal */}
      {tempPasswordResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="legacy-temp-pwd-modal">
            <div className="flex items-center gap-2 mb-3">
              <Key className="h-6 w-6 text-purple-600" />
              <h3 className="text-lg font-semibold">Contraseña temporal generada</h3>
            </div>
            <p className="text-sm text-slate-600 mb-3">
              Se envió una contraseña temporal por email a <strong>{tempPasswordResult.email}</strong>.
            </p>
            {tempPasswordResult.email_sent === false && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-900 mb-3">
                ⚠️ El email NO se pudo enviar. Revise los logs del servidor para ver la contraseña.
              </div>
            )}
            <div className="flex justify-end">
              <button onClick={() => setTempPasswordResult(null)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
